<template>
  <bm-number-q
    class="bm-currency"
    v-bind="$attrs"
    :min="min"
    :max-decimal-places="maxDecimalPlacesComputed"
    :fixed-decimal-places="true"
    :prefix="prefixComputed"
    :suffix="suffixComputed"
    :value="value"
    @update:value="emit('update:value', $event)"
  ></bm-number-q>
</template>

<script setup lang="ts">
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { EnumCurrency } from "@/app/services/generated/enums/enumCurrency.generated";
import {
  getCurrencySuffix,
  getCurrencyDefinition,
  getCurrencyPrefix,
} from "@/app/types/common/currency";
import { computed } from "vue";

interface BmCurrencyProps {
  value?: number;
  prefix?: string;
  suffix?: string;
  maxDecimalPlaces?: number;
  min?: number;
  quantise?: boolean;
  overrideCurrency?: EnumCurrency;
}

interface BmCurrencyEvents {
  (event: "update:value", value: number | null): void;
}

const props = withDefaults(defineProps<BmCurrencyProps>(), {
  min: 0,
  quantise: false,
  overrideCurrency: () => EnumCurrency.none,
});

const emit = defineEmits<BmCurrencyEvents>();

const suffixComputed = computed(
  () => props.suffix || getCurrencySuffix(props.overrideCurrency)
);

const prefixComputed = computed(
  () => props.prefix || getCurrencyPrefix(props.overrideCurrency)
);

const maxDecimalPlacesComputed = computed(() => {
  const quantisedMaxDecimal =
    getCurrencyDefinition(props.overrideCurrency)
      ?.currencyQuantisedMaxDecimalPlaces ??
    tenantService.settings.currency.quantisedMaxDecimalPlaces;

  return props.maxDecimalPlaces || props.quantise
    ? quantisedMaxDecimal
    : (getCurrencyDefinition(props.overrideCurrency)
        ?.currencyMaxDecimalPlaces ??
        tenantService.settings.currency.maxDecimalPlaces);
});
</script>

<style lang="sass" scoped>
.bm-number
  max-width: 10em
</style>
